import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { action, computed, observable } from "mobx";
import { VrConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Child, Room, Travelers } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { setCookie } from "components/utility/cookie";
import { InfantSeating } from "components/shared/TravelersField";
import { LastSearchData, VRLastSearchData } from "stores/wizard/LastSearchData";

export class VrWizardState implements LOBState {
  private globalState: GlobalWizardState;
  public wizardInput: React.RefObject<HTMLInputElement>;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return (this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB) && this.isDesktop;
  }

  public lastSearchData?: VRLastSearchData;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 2,
        children: [],
        infants: [],
      },
    ];

    // Loop through existing room Info for travelers to find applicable room
    const applicableRoomsForTravelers: Room[] = this.globalState.travelers.hotel.rooms.map(
      (room: Room, roomIndex: number) => {
        const applicableAdultsPerRoom =
          room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;

        const combinedChildren = room.children.concat(room.infants);
        const applicableChildrenPerRoom =
          combinedChildren.length > this.config.travelers.maxChildrenPerRoom
            ? combinedChildren.splice(0, this.config.travelers.maxChildrenPerRoom)
            : combinedChildren;

        return {
          adults: applicableAdultsPerRoom,
          children: applicableChildrenPerRoom,
          infants: [],
        };
      }
    );

    // Only the first room details to be preserved in nonHotel [Room]
    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const vrTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
      cruise: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
    };

    return vrTravelers;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @observable
  public config: VrConfig;

  @observable
  public subLOBState: string = "";

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @computed
  public get date() {
    return this.globalState.date;
  }

  // validations
  @observable
  public destinationInvalidKey: string | "";

  @action
  public validateDestinationField = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;
      this.wizardInput?.current?.focus();

      return false;
    }

    this.destinationInvalidKey = "";

    return true;
  };

  @observable
  public hotelTravelersInvalidKey: string = "";

  @action
  public validateLessThanNTravelers = () => {
    const isValid = this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelers);
    if (!isValid) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @action
  public validateMaxDateRange = () => {
    if (!this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)) {
      this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  public validateForm = () => {
    const validations: boolean[] = [
      this.validateDestinationField(),
      this.validateTravelersField(),
      this.validateEndDateField(),
    ];

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string
  ) {
    this.globalState.updateDestinationFromCookie(value, regionID, countryCode, latLong, ttla, destinationID, hotelID);
    this.destinationInvalidKey = "";
  }

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const destination = this.location.destination;
      const destinationMetaData = this.location.destination.metaData;
      const date = this.date;
      const travelersHotel = this.travelers.hotel;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        vr: {
          destination: {
            value: destination.value ? encodeURIComponent(destination.value) : "",
            regionID: destinationMetaData.regionId ? encodeURIComponent(destinationMetaData.regionId) : "",
            ttla: destinationMetaData.ttla ? encodeURIComponent(destinationMetaData.ttla) : "",
            countryCode: destinationMetaData.countryCode ? encodeURIComponent(destinationMetaData.countryCode) : "",
            latLong: destinationMetaData.latLong ? encodeURIComponent(destinationMetaData.latLong) : "",
            destinationID: destinationMetaData.destinationId
              ? encodeURIComponent(destinationMetaData.destinationId)
              : "",
            hotelID: destinationMetaData.hotelId ? destinationMetaData.hotelId : "",
          },
          date: {
            start: date.start ? date.start.toString() : "",
            end: date.end ? date.end.toString() : "",
          },
          travelers: JSON.stringify(travelersHotel.rooms),
          infantSeating: travelersHotel.infantSeating,
        },
      };

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
      });
    }
  };

  public updateStateFromCookie = () => {
    if (this.canTrack && this.useLastSearch && this.lastSearchData) {
      const destinationValue = decodeURIComponent(this.lastSearchData.destination?.value);
      const startDateFromCookie = this.lastSearchData.date?.start
        ? new Date(this.lastSearchData.date.start)
        : new Date();
      const endDateFromCookie = this.lastSearchData.date?.end ? new Date(this.lastSearchData.date.end) : new Date();
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      const travelers: Travelers = this.lastSearchData.travelers
        ? {
            rooms: JSON.parse(this.lastSearchData.travelers),
            infantSeating: this.lastSearchData.infantSeating === "seat" ? InfantSeating.IN_SEAT : InfantSeating.ON_LAP,
          }
        : this.travelers.hotel;

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        this.lastSearchData.destination?.regionID ? this.lastSearchData.destination.regionID : "",
        this.lastSearchData.destination?.countryCode ? this.lastSearchData.destination.countryCode : "",
        this.lastSearchData.destination?.latLong ? this.lastSearchData.destination.latLong : "",
        this.lastSearchData.destination?.ttla ? this.lastSearchData.destination.ttla : "",
        this.lastSearchData.destination?.destinationID ? this.lastSearchData.destination.destinationID : "",
        this.lastSearchData.destination?.hotelID ? this.lastSearchData.destination.destinationID : ""
      );
      this.updateDateSelection(startDateToUse, endDateToUse);

      this.updateHotelTravelersSelection(travelers);
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
    }
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    const rooms = travelers.rooms.map((room) => {
      const [roomInfants, roomChildren] = room.children.reduce(
        ([infants, children]: [Child[], Child[]], child) => {
          child.age < 2 ? infants.push(child) : children.push(child);

          return [infants, children];
        },
        [[], []]
      );

      return {
        adults: room.adults,
        children: roomChildren,
        infants: roomInfants,
      };
    });
    this.globalState.updateHotelTravelersSelection({
      rooms,
      infantSeating: travelers.infantSeating,
    });
    this.hotelTravelersInvalidKey = "";
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  constructor(config: VrConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.vr;
    }
  }
}
